import { Cancel01Icon, Search01Icon } from '@hugeicons/core-free-icons';
import { Icon } from './icon.js';
import * as React from 'react';
import { cn } from '../../lib/cn.js';

/** Shared look of text-like controls: 40px high, control border, visible focus outline. */
export const controlBase = cn(
  'h-10 w-full min-w-0 rounded-control border border-border-control bg-surface-default px-3 text-body text-text-primary',
  'placeholder:text-text-secondary transition-colors duration-[var(--ef-motion-fast)]',
  'hover:border-text-secondary focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-focus',
  'disabled:cursor-not-allowed disabled:bg-surface-subtle disabled:opacity-60',
  'aria-invalid:border-danger aria-invalid:outline-danger',
);

export type InputProps = React.InputHTMLAttributes<HTMLInputElement>;

export const Input = React.forwardRef<HTMLInputElement, InputProps>(function Input({ className, ...props }, ref) {
  return <input ref={ref} className={cn(controlBase, className)} {...props} />;
});

export type TextareaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement>;

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea({ className, ...props }, ref) {
  return <textarea ref={ref} className={cn(controlBase, 'h-auto min-h-24 py-2 leading-5', className)} {...props} />;
});

export interface SearchInputProps extends Omit<InputProps, 'type' | 'onChange' | 'value'> {
  value: string;
  onValueChange: (value: string) => void;
  /** Accessible label (also the placeholder when none given). */
  label?: string;
}

export const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(function SearchInput(
  { value, onValueChange, label = 'Buscar', placeholder, className, ...props },
  ref,
) {
  return (
    <div className={cn('relative w-full', className)}>
      <Icon icon={Search01Icon} aria-hidden className="pointer-events-none absolute left-3 top-1/2 size-[18px] -translate-y-1/2 text-text-secondary" />
      <input
        ref={ref}
        type="search"
        aria-label={label}
        placeholder={placeholder ?? label}
        value={value}
        onChange={(e) => onValueChange(e.target.value)}
        className={cn(controlBase, 'pl-10 pr-10 [&::-webkit-search-cancel-button]:hidden')}
        {...props}
      />
      {value ? (
        <button
          type="button"
          aria-label="Limpiar búsqueda"
          onClick={() => onValueChange('')}
          className="absolute right-2 top-1/2 inline-flex size-7 -translate-y-1/2 items-center justify-center rounded-control text-text-secondary hover:bg-surface-subtle hover:text-text-primary focus-visible:outline-2 focus-visible:outline-focus"
        >
          <Icon icon={Cancel01Icon} aria-hidden className="size-4" />
        </button>
      ) : null}
    </div>
  );
});
